import React from 'react'
import { QueryCache, QueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'

const STALE_NOTICE = 'stale-data'

// Why a refresh failed, in words a person can act on.
const reason = (error: any) => {
  if (error?.status == null) return 'the connection dropped'
  if (error.status >= 500) return `the server had a problem (${error.status})`
  return `the server refused the request (${error.status})`
}

export const createQueryClient = () => {
  const client: QueryClient = new QueryClient({
    // A failed refetch keeps the page's last data (throwOnError below), so say so and
    // why, with a way to try again. One notice, replaced rather than stacked while a
    // poll keeps failing, and gone once anything refreshes. A query with no data shows
    // the error screen instead, and a 401 goes to sign-in.
    queryCache: new QueryCache({
      onError: (error: any, query) => {
        if (query.state.data === undefined || error?.status === 401) return
        toast.error(
          <span className="flex items-center gap-3">
            <span>Could not refresh: {reason(error)}. What you see may be out of date.</span>
            <button
              type="button"
              className="shrink-0 font-semibold underline"
              onClick={() => client.refetchQueries({ predicate: (failed) => failed.state.status === 'error' })}
            >
              Try again
            </button>
          </span>,
          { id: STALE_NOTICE, duration: Infinity },
        )
      },
      onSuccess: () => toast.dismiss(STALE_NOTICE),
    }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        // A phone that drops a request should keep the page it has rather than swap it
        // for an error screen. A request that got no HTTP answer is tried twice more;
        // HTTP errors are not retried. Only a query with nothing to show yet, or a
        // lapsed session (401, which the boundary turns into a sign-in), reaches the
        // error boundary; a failed background refetch keeps its last data.
        retry: (failureCount: number, error: any) => error?.status == null && failureCount < 2,
        throwOnError: (error: any, query) => error?.status === 401 || query.state.data === undefined,
      },
      mutations: {
        throwOnError: (error: any) => error?.status !== 401,
      },
    },
  })
  return client
}
